import React, { useState, useRef, useEffect } from "react";
import { Typography, IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import RepeatIcon from "@mui/icons-material/Repeat";
import Loader from "../assets/Loader.png";
import LoaderAnimation from "../assets/LoaderAnimation.png";
import "../App.css";

export default function FlowPressureSelector({
  pressures,
  flow,
  powerLevelsKW = [1, 5, 15, 30, 50, 80],
  powerLevelsHP = [1, 7, 20, 40, 67, 107],
  motorCountsOfPressureFlow = {},
  onSelectionChange,
  measurementUnit,
  selectedToggle,
  loader,
}) {
  let validPoints, contours, pts;
  const height = 500;
  const width = 700;
  const DEFAULT_POINTS = [{ x: 125, y: 180 }];
  const DEFAULT_POINTS_IMPERIAL = [{ x: 25, y: 2611 }];
  const defaultPoint =
    measurementUnit === "Imperial"
      ? DEFAULT_POINTS_IMPERIAL[0]
      : DEFAULT_POINTS[0];

  const containerRef = useRef();

  useEffect(() => {
    setSel(null);
  }, [measurementUnit]);
  const xs = Array.from(new Set(flow.map((v) => Math.round(v)))).sort(
    (a, b) => a - b
  );
  const ys = Array.from(new Set(pressures.map((v) => Math.round(v)))).sort(
    (a, b) => a - b
  );
  const allPoints = xs.flatMap((x) => ys.map((y) => ({ x, y })));
  const maxKW = Math.max(...powerLevelsKW);
  const maxHp = Math.max(...powerLevelsHP);
  if (measurementUnit === "Metric") {
    validPoints = allPoints.filter((pt) => (pt.x * pt.y) / 600 <= maxKW);
  } else if (measurementUnit === "Imperial") {
    validPoints = allPoints.filter((pt) => (pt.x * pt.y) / 1714 <= maxHp);
  }

  const roundTick = (v) =>
    v < 100 ? Math.round(v / 10) * 10 : Math.round(v / 100) * 100;
  const nt = 5;
  const xMin = xs[0],
    xMax = xs[xs.length - 1];
  const yMin = ys[0],
    yMax = ys[ys.length - 1];
  const xTicks = Array.from(
    new Set(
      Array.from({ length: nt }, (_, i) =>
        roundTick(xMin + ((xMax - xMin) * i) / (nt - 1))
      )
    )
  ).sort((a, b) => a - b);
  const yTicks = Array.from(
    new Set(
      Array.from({ length: nt }, (_, i) =>
        roundTick(yMin + ((yMax - yMin) * i) / (nt - 1))
      )
    )
  ).sort((a, b) => a - b);

  if (measurementUnit === "Metric") {
    contours = powerLevelsKW.map((pKW) => {
      pts = [
        ...xs.map((rpm) => ({ x: rpm, y: (pKW * 600) / rpm })),
        ...ys.map((tq) => ({ x: (pKW * 600) / tq, y: tq })),
      ].filter((p) => p.x >= xMin && p.x <= xMax && p.y >= yMin && p.y <= yMax);
      const uniq = Array.from(
        new Map(pts.map((p) => [`${p.x}-${p.y}`, p])).values()
      ).sort((a, b) => a.x - b.x);
      return { pKW, pts: uniq };
    });
  } else if (measurementUnit === "Imperial") {
    contours = powerLevelsHP.map((pHp) => {
      const pts = [
        ...xs.map((rpm) => ({ x: rpm, y: (pHp * 1714) / rpm })),
        ...ys.map((tq) => ({ x: (pHp * 1714) / tq, y: tq })),
      ].filter((p) => p.x >= xMin && p.x <= xMax && p.y >= yMin && p.y <= yMax);
      const uniq = Array.from(
        new Map(pts.map((p) => [`${p.x}-${p.y}`, p])).values()
      ).sort((a, b) => a.x - b.x);
      return { pHp, pts: uniq };
    });
  }

  const pad = { top: 20, right: 20, bottom: 60, left: 80 };
  const iW = width - pad.left - pad.right;
  const iH = height - pad.top - pad.bottom;
  const xS = (v) => pad.left + ((v - xMin) / (xMax - xMin)) * iW;
  const yS = (v) => height - pad.bottom - ((v - yMin) / (yMax - yMin)) * iH;

  const xsCells = xs.slice(0, xs.length - 1);
  const ysCells = ys.slice(0, ys.length - 1);
  const minOp = 0.1,
    maxOp = 0.6;
  const getOpacity = (count) => {
    if (count <= 0) return 0;
    const bucket = Math.min(count, 10);
    return minOp + ((bucket - 1) / 9) * (maxOp - minOp);
  };

  const [hoverPt, setHoverPt] = useState(null);
  const [sel, setSel] = useState(null);
  const [drag, setDrag] = useState(false);
  const ref = useRef();

  function nearest(mx, my) {
    let best,
      md = Infinity;
    validPoints.forEach((p) => {
      const dx = xS(p.x) - mx,
        dy = yS(p.y) - my;
      const d2 = dx * dx + dy * dy;
      if (d2 < md) {
        md = d2;
        best = p;
      }
    });
    return best;
  }

  const onMove = (e) => {
    const { left, top } = ref.current.getBoundingClientRect();
    setHoverPt(nearest(e.clientX - left, e.clientY - top));
  };
  const onLeave = () => setHoverPt(null);
  const selectPt = (p) => {
    setSel(p);
    onSelectionChange?.(p);
  };
  const onDown = () =>
    hoverPt &&
    sel &&
    sel.x === hoverPt.x &&
    sel.y === hoverPt.y &&
    setDrag(true);
  const onUp = () => {
    drag && hoverPt && selectPt(hoverPt);
    setDrag(false);
  };
  const onClick = () => {
    hoverPt && selectPt(hoverPt);
  };
  const originX = pad.left;
  const originY = height - pad.bottom;
  return loader ? (
    <div className="loader-container">
      <div className="dot-loader">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
    </div>
  ) : (
    <div
      style={{
        overflowY: "hidden",
      }}
    >
      <div
        ref={containerRef}
        height={500}
        style={{ width: "100%", overflowX: "hidden" }}
      >
        <svg
          ref={ref}
          width={width}
          height={500}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          onMouseDown={onDown}
          onMouseUp={onUp}
          onClick={onClick}
          className="bg-white border"
          role="application"
          aria-labelledby="ttl"
        >
          <title id="ttl">Flow-Pressure Selector</title>
          <text
            transform={`translate(${pad.left - 72}, ${
              pad.top + iH / 2
            }) rotate(-90)`}
            textAnchor="middle"
            fontSize={12}
            fontWeight="bold"
            fontFamily="Myriad Pro, Source Sans Pro, system-ui, sans-serif"
            style={{ cursor: "pointer" }}
          >
            {selectedToggle === "FlowPressure"
              ? measurementUnit === "Imperial"
                ? "Pressure [psi]"
                : "Pressure [bar]"
              : measurementUnit === "Imperial"
              ? "Torque [lbf·in]"
              : "Torque [Nm]"}
          </text>
          <text
            transform={`translate(${pad.left + iW / 2}, ${height - 5})`}
            textAnchor="middle"
            fontSize={12}
            fontWeight="bold"
            fontFamily="Myriad Pro, Source Sans Pro, system-ui, sans-serif"
            style={{ cursor: "pointer" }}
          >
            {selectedToggle === "FlowPressure"
              ? measurementUnit === "Imperial"
                ? "Flow [gal/min]"
                : "Flow [l/min]"
              : "Rotational velocity [RPM]"}
          </text>
          {xsCells.map((x, i) =>
            ysCells.map((y, j) => {
              const key = `${x}-${y}`;
              const count = motorCountsOfPressureFlow[key] || 0;
              if (count === 0) return null;
              const x0 = xS(x),
                x1 = xS(xs[i + 1]);
              const y0 = yS(y),
                y1 = yS(ys[j + 1]);
              return (
                <rect
                  key={`heat-${x}-${y}`}
                  x={x0}
                  y={y1}
                  width={x1 - x0}
                  height={y0 - y1}
                  fill="#ac070c"
                  fillOpacity={getOpacity(count)}
                  pointerEvents="none"
                />
              );
            })
          )}
          {xsCells.map((x, i) => {
            const x0 = xS(x);
            const x1 = xS(xs[i + 1] ?? xs[i] + (xs[1] - xs[0]));

            const yBelow = ys[0] - (ys[1] - ys[0]);
            const y0 = yS(yBelow);
            const y1 = yS(ys[0]);
            return (
              <rect
                key={`empty-bottom-${x}`}
                x={x0}
                y={y1}
                width={x1 - x0}
                height={y0 - y1}
                fill="#fff"
                stroke="#ccc"
                strokeWidth={0.5}
                pointerEvents="none"
              />
            );
          })}

          {ysCells.map((y, j) => {
            const y0 = yS(y);
            const y1 = yS(ys[j + 1] ?? ys[j] - (ys[1] - ys[0]));
            const x0 = xS(xs[0] - (xs[1] - xs[0]));
            const x1 = xS(xs[0]);
            return (
              <rect
                key={`empty-left-${y}`}
                x={x0}
                y={y1}
                width={x1 - x0}
                height={y0 - y1}
                fill="#fff"
                stroke="#ccc"
                strokeWidth={0.5}
                pointerEvents="none"
              />
            );
          })}
          {xs.map((x) => {
            const yVals = validPoints
              .filter((pt) => pt.x === x)
              .map((pt) => pt.y);
            if (!yVals.length) return null;
            const yp = yVals.map(yS);
            return (
              <line
                key={`xgrid-${x}`}
                x1={xS(x)}
                y1={Math.min(...yp)}
                x2={xS(x)}
                y2={Math.max(...yp)}
                stroke="#ccc"
                strokeWidth={0.5}
              />
            );
          })}
          {ys.map((y) => {
            const xVals = validPoints
              .filter((pt) => pt.y === y)
              .map((pt) => pt.x);
            if (!xVals.length) return null;
            const xp = xVals.map(xS);
            return (
              <line
                key={`ygrid-${y}`}
                x1={Math.min(...xp)}
                y1={yS(y)}
                x2={Math.max(...xp)}
                y2={yS(y)}
                stroke="#ccc"
                strokeWidth={0.5}
              />
            );
          })}
          {measurementUnit === "Metric"
            ? contours.map((c) => {
                const midIndex = Math.floor(c.pts.length / 2);
                const midPoint = c.pts[midIndex];

                return (
                  <g key={c.pKW}>
                    <path
                      d={c.pts
                        .map((p, i) => `${i ? "L" : "M"} ${xS(p.x)} ${yS(p.y)}`)
                        .join(" ")}
                      stroke="#000"
                      strokeWidth={1}
                      strokeDasharray="4 2"
                      fill="none"
                    />
                    {midPoint && (
                      <text
                        x={xS(midPoint.x) + 5}
                        y={yS(midPoint.y) - 5}
                        fontSize={10}
                        fill="#000"
                        style={{
                          userSelect: "none",
                          WebkitUserSelect: "none",
                          MozUserSelect: "none",
                          msUserSelect: "none",
                          pointerEvents: "none",
                        }}
                      >
                        {c.pKW} kW
                      </text>
                    )}
                  </g>
                );
              })
            : contours.map((c) => {
                const midIndex = Math.floor(c.pts.length / 2);
                const midPoint = c.pts[midIndex];

                return (
                  <g key={c.pHp}>
                    <path
                      d={c.pts
                        .map((p, i) => `${i ? "L" : "M"} ${xS(p.x)} ${yS(p.y)}`)
                        .join(" ")}
                      stroke="#000"
                      strokeWidth={1}
                      strokeDasharray="4 2"
                      fill="none"
                    />
                    {midPoint && (
                      <text
                        x={xS(midPoint.x) + 5}
                        y={yS(midPoint.y) - 5}
                        fontSize={10}
                        fill="#000"
                        style={{
                          userSelect: "none",
                          WebkitUserSelect: "none",
                          MozUserSelect: "none",
                          msUserSelect: "none",
                          pointerEvents: "none",
                        }}
                      >
                        {c.pHp} HP
                      </text>
                    )}
                  </g>
                );
              })}
          {xTicks.map((t) => (
            <text
              key={`xt-${t}`}
              x={xS(t)}
              y={originY + 25}
              textAnchor="middle"
              alignmentBaseline="hanging"
              fontSize={10}
            >
              {t}
            </text>
          ))}

          {/* Y-axis ticks */}
          {yTicks.map((t) => (
            <text
              key={`yt-${t}`}
              x={originX - 35}
              y={yS(t)}
              textAnchor="end"
              alignmentBaseline="middle"
              fontSize={10}
            >
              {t}
            </text>
          ))}

          {sel ? (
            <g>
              <line
                x1={xS(sel.x)}
                y1={pad.top}
                x2={xS(sel.x)}
                y2={height - pad.bottom}
                stroke="#000"
                strokeWidth={1}
              />
              <line
                x1={pad.left}
                y1={yS(sel.y)}
                x2={width - pad.right}
                y2={yS(sel.y)}
                stroke="#000"
                strokeWidth={1}
              />
              <circle
                cx={xS(sel.x)}
                cy={yS(sel.y)}
                r={6}
                stroke="currentColor"
                strokeWidth={2}
                fill="none"
              />
              {/* Adjusted X-axis readout */}
              <rect
                x={xS(sel.x) - 20}
                y={height - pad.bottom + 25}
                width={40}
                height={20}
                fill="#000"
              />
              <text
                x={xS(sel.x)}
                y={height - pad.bottom + 40}
                textAnchor="middle"
                fill="#fff"
                fontSize={12}
              >
                {sel.x}
              </text>
              {/* Adjusted Y-axis readout */}
              <rect
                x={pad.left - 70}
                y={yS(sel.y) - 10}
                width={40}
                height={20}
                fill="#000"
              />
              <text
                x={pad.left - 50}
                y={yS(sel.y) + 5}
                textAnchor="middle"
                fill="#fff"
                fontSize={12}
              >
                {sel.y}
              </text>
            </g>
          ) : (
            <g>
              <line
                x1={xS(defaultPoint.x)}
                y1={pad.top}
                x2={xS(defaultPoint.x)}
                y2={height - pad.bottom}
                stroke="#000"
                strokeWidth={1}
              />
              <line
                x1={pad.left}
                y1={yS(defaultPoint.y)}
                x2={width - pad.right}
                y2={yS(defaultPoint.y)}
                stroke="#000"
                strokeWidth={1}
              />
              <circle
                cx={xS(defaultPoint.x)}
                cy={yS(defaultPoint.y)}
                r={6}
                stroke="currentColor"
                strokeWidth={2}
                fill="none"
              />
              {/* Adjusted X-axis readout */}
              <rect
                x={xS(defaultPoint.x) - 20}
                y={height - pad.bottom + 25}
                width={40}
                height={20}
                fill="#000"
              />
              <text
                x={xS(defaultPoint.x)}
                y={height - pad.bottom + 40}
                textAnchor="middle"
                fill="#fff"
                fontSize={12}
              >
                {defaultPoint.x}
              </text>
              {/* Adjusted Y-axis readout */}
              <rect
                x={pad.left - 70}
                y={yS(defaultPoint.y) - 10}
                width={40}
                height={20}
                fill="#000"
              />
              <text
                x={pad.left - 50}
                y={yS(defaultPoint.y) + 5}
                textAnchor="middle"
                fill="#fff"
                fontSize={12}
              >
                {defaultPoint.y}
              </text>
            </g>
          )}

          {hoverPt && (
            <g>
              <line
                x1={xS(hoverPt.x)}
                y1={pad.top}
                x2={xS(hoverPt.x)}
                y2={height - pad.bottom}
                stroke="rgb(215,25,32)"
                strokeWidth={1}
              />
              <line
                x1={pad.left}
                y1={yS(hoverPt.y)}
                x2={width - pad.right}
                y2={yS(hoverPt.y)}
                stroke="rgb(215,25,32)"
                strokeWidth={1}
              />
              {/* RPM */}
              <rect
                x={xS(hoverPt.x) - 20}
                y={height - pad.bottom + 15}
                width={40}
                height={20}
                fill="rgb(215,25,32)"
              />
              <text
                x={xS(hoverPt.x)}
                y={height - pad.bottom + 30}
                textAnchor="middle"
                fill="#fff"
                fontSize={12}
              >
                {hoverPt.x}
              </text>
              {/* Torque */}
              <rect
                x={pad.left - 50}
                y={yS(hoverPt.y) - 10}
                width={40}
                height={20}
                fill="rgb(215,25,32)"
              />
              <text
                x={pad.left - 30}
                y={yS(hoverPt.y) + 5}
                textAnchor="middle"
                fill="#fff"
                fontSize={12}
              >
                {hoverPt.y}
              </text>
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}
